import { useState } from 'react'

import { ActionIcon, Loader, Tooltip } from '@mantine/core'

import CallerIdShield, { callerIdStatusLabel } from './CallerIdShield'
import CallerIdVerificationModal from './CallerIdVerificationModal'
import {
  useVerifiedCallerId,
  type VerifiedCallerIdTarget,
} from './useVerifiedCallerId'

interface CallerIdStatusIconProps extends VerifiedCallerIdTarget {
  /** Who places the calls, for the modal copy. */
  caller: 'you' | 'they' | 'your team'
  /** Whether the viewer may start or remove a verification. */
  canManage: boolean
  /** Icon size in px; 14 next to xs text, 16 in tables. */
  size?: number
  className?: string
}

/**
 * A small shield next to a phone number showing whether it is a verified
 * outbound caller ID. Clicking it opens the verification walkthrough.
 */
const CallerIdStatusIcon = ({
  ownerType,
  employeeId,
  phoneNumber,
  caller,
  canManage,
  size = 16,
  className,
}: CallerIdStatusIconProps) => {
  const [opened, setOpened] = useState(false)
  const callerId = useVerifiedCallerId(
    { ownerType, employeeId, phoneNumber },
    { skip: !phoneNumber }
  )

  if (!phoneNumber) {
    return null
  }

  if (callerId.loading) {
    return <Loader size={size} color="gray" className={className} />
  }

  const label = callerIdStatusLabel(callerId.status)

  return (
    <>
      <Tooltip label={`${label}. Click for details`} withArrow>
        <ActionIcon
          size={size}
          variant="subtle"
          aria-label={label}
          onClick={() => setOpened(true)}
          className={className}
        >
          <CallerIdShield status={callerId.status} size={size} />
        </ActionIcon>
      </Tooltip>
      <CallerIdVerificationModal
        opened={opened}
        onClose={() => setOpened(false)}
        phoneNumber={phoneNumber}
        caller={caller}
        currentRow={callerId.currentRow}
        staleRow={callerId.staleRow}
        canManage={canManage}
        busy={callerId.busy}
        onRequest={callerId.request}
        onRefresh={callerId.refetch}
        onRemove={(row) => {
          callerId.remove(row)
          setOpened(false)
        }}
      />
    </>
  )
}

export default CallerIdStatusIcon
